import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./diet_panel.scss";
import ReactQuill from "react-quill";
import parse from "html-react-parser";

interface UserDetails {
  name: string;
  email: string;
  phoneNumber: string;
  role: string;
  gender: string;
}

interface DietPlan {
  _id: string;
  dietCategory: string;
  dietPlan: { mealType: string; diet: string }[];
  isPureVeg: boolean;
  language: string;
}

const DietPanel: React.FC = () => {
  const [role, setRole] = useState<string>("");
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [dietPlans, setDietPlans] = useState<DietPlan[]>([]);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);
  const [dietPlanToDelete, setDietPlanToDelete] = useState<string | null>(null);
  const [showCreateDietPopup, setShowCreateDietPopup] =
    useState<boolean>(false);
  const [newDietCategory, setNewDietCategory] = useState<string>("");
  const [isPureVegDiet, setIsPureVegDit] = useState<boolean>(false);
  const [newMealPlans, setNewMealPlans] = useState<
    { mealType: string; diet: string }[]
  >([]);
  const [editDietPlan, setEditDietPlan] = useState<DietPlan | null>(null);
  const [expandedPlans, setExpandedPlans] = useState<Record<string, boolean>>(
    {}
  );

  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedFilterLanguage, setSelectedFilterLanguage] = useState("all");

  const languages = [
    { code: "en", name: "English" },
    { code: "hi", name: "हिन्दी (Hindi)" },
    { code: "bn", name: "বাংলা (Bangla)" },
    { code: "mr", name: "मराठी (Marathi)" },
    { code: "pa", name: "ਪੰਜਾਬੀ (Punjabi)" },
    { code: "te", name: "తెలుగు (Telugu)" },
    { code: "ta", name: "தமிழ் (Tamil)" },
    { code: "gu", name: "ગુજરાતી (Gujrati)" },
    { code: "ur", name: "اردو (Urdu)" },
    { code: "kn", name: "ಕನ್ನಡ (Kannada)" },
    { code: "or", name: "ଓଡିଆ (Odia)" },
    { code: "ml", name: "മലയാളം (Malayalam)" },
    { code: "as", name: "অসমীয়া (Assamese)" },
  ];

  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  const toggleExpandPlan = (id: string) => {
    setExpandedPlans((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleVegToggle = () => {
    setIsPureVegDit(!isPureVegDiet);
  };

  // Fetch user details
  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");

      if (!email || !token) {
        navigate("/login");
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );

      setUserDetails(response.data);
      fetchAllDietPlans();
      setRole(response.data.role);
    } catch (error) {
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  // Fetch all diet plans
  const fetchAllDietPlans = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-diet-plans`
      );
      setDietPlans(response.data.data);
    } catch (error) {
      console.error("Failed to fetch diet plans:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle diet plan deletion
  const handleDeleteDietPlan = async (id: string) => {
    try {
      await axios.delete(`https://api.fitoja.in/api/delete-diet-plan/${id}`);
      setDietPlans(dietPlans.filter((plan) => plan._id !== id));
      setShowDeleteConfirmation(false);
      setDietPlanToDelete(null);
    } catch (error) {
      console.error("Failed to delete diet plan:", error);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    navigate("/login");
  };

  // Handle diet plan creation
  const handleCreateDietPlan = async () => {
    try {
      const newDiet = {
        dietCategory: newDietCategory,
        dietPlan: newMealPlans,
        isPureVeg: isPureVegDiet,
        language: selectedLanguage,
      };
      await axios.post(`https://api.fitoja.in/api/create-diet-plan`, newDiet);
      fetchAllDietPlans();
      setShowCreateDietPopup(false);
      setNewDietCategory("");
      setIsPureVegDit(false);
      setNewMealPlans([]);
      setSelectedLanguage("");
    } catch (error) {
      console.error("Failed to create diet plan:", error);
    }
  };

  // Handle diet plan update
  const handleUpdateDietPlan = async () => {
    if (!editDietPlan) return;

    try {
      const updatedDiet = {
        id: editDietPlan._id,
        dietCategory: newDietCategory,
        dietPlan: newMealPlans,
        isPureVeg: isPureVegDiet,
        language: selectedLanguage,
      };
      await axios.post(
        `https://api.fitoja.in/api/create-diet-plan`,
        updatedDiet
      );
      fetchAllDietPlans();
      setEditDietPlan(null);
      setNewDietCategory("");
      setIsPureVegDit(false);
      setNewMealPlans([]);
      setSelectedLanguage("");
    } catch (error) {
      console.error("Failed to update diet plan:", error);
    }
  };

  // Add new meal plan
  const addMeal = () =>
    setNewMealPlans([...newMealPlans, { mealType: "", diet: "" }]);

  // Delete meal plan by index
  const deleteMeal = (index) => {
    setNewMealPlans((prevMealPlans) =>
      prevMealPlans.filter((_, i) => i !== index)
    );
  };

  // Update a meal plan
  const updateMeal = (index, field, value) => {
    setNewMealPlans((prevMealPlans) =>
      prevMealPlans.map((meal, i) =>
        i === index ? { ...meal, [field]: value } : meal
      )
    );
  };

  // Open Edit Popup
  const openEditPopup = (plan: DietPlan) => {
    setEditDietPlan(plan);
    setNewDietCategory(plan.dietCategory);
    setIsPureVegDit(plan.isPureVeg);
    setNewMealPlans(plan.dietPlan);
    setSelectedLanguage(plan.language != null ? plan.language : "");
  };

  // Fetch user details on component mount
  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  return (
    <div className="diet-panel-container">
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} alt="Logo" />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                  alt="Close"
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}
          <br />
          <br />
          <br />
          <br />
          <h3>Create/Edit/Delete Diets</h3>
          <button
            className="create-diet-button"
            onClick={() => setShowCreateDietPopup(true)}
          >
            + Create New Diet
          </button>

          {/* Language filter buttons */}
          <div className="language-filter-container">
            <div className="language-buttons">
              <button
                className={`language-button ${
                  selectedFilterLanguage === "all" ? "active" : ""
                }`}
                onClick={() => setSelectedFilterLanguage("all")}
              >
                All Languages{" "}
                <span style={{ fontWeight: "700" }}>[{dietPlans.length}]</span>
              </button>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className={`language-button ${
                    selectedFilterLanguage === lang.code ? "active" : ""
                  }`}
                  onClick={() => setSelectedFilterLanguage(lang.code)}
                >
                  {lang.name} {" "}
                  <span style={{ fontWeight: "700" }}>
                    [
                    {
                      dietPlans.filter((plan) => plan.language === lang.code)
                        .length
                    }
                    ]
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="diet-plans-list">
            {dietPlans
              .filter(
                (plan) =>
                  selectedFilterLanguage === "all" ||
                  plan.language === selectedFilterLanguage
              )
              .map((plan) => {
                const isExpanded = expandedPlans[plan._id];
                const displayMeals = isExpanded
                  ? plan.dietPlan
                  : plan.dietPlan.slice(0, 1); // Show only 1 meal by default

                return (
                  <div key={plan._id} className="diet-plan-item">
                    <div className="diet-plan-header">
                      <h4>{plan.dietCategory}</h4>
                      {plan.dietPlan.length > 1 && (
                        <button
                          className="expand-toggle"
                          onClick={() => toggleExpandPlan(plan._id)}
                        >
                          {isExpanded ? "Show Less" : "Show More"}
                        </button>
                      )}
                    </div>
                    <div className="meal-items-container">
                      {displayMeals.map((meal, index) => (
                        <p key={index} className="meal-item">
                          <strong className="meal-type">
                            {parse(meal.mealType)}:
                          </strong>{" "}
                          {parse(meal.diet)}
                        </p>
                      ))}
                    </div>
                    {plan.dietPlan.length > 1 && (
                      <div className="meal-count-indicator">
                        {!isExpanded &&
                          `+${plan.dietPlan.length - 1} more meals`}
                      </div>
                    )}
                    <div className="diet-plan-actions">
                      <button
                        className="edit-button"
                        onClick={() => openEditPopup(plan)}
                      >
                        Edit
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => {
                          setShowDeleteConfirmation(true);
                          setDietPlanToDelete(plan._id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>

          {showDeleteConfirmation && (
            <div className="delete-modal">
              <div className="delete-modal-content">
                <h2>Delete Confirmation</h2>
                <p>Are you sure you want to delete this diet Plan?</p>
                <div style={{ display: "flex", gap: "20px" }}>
                  <button
                    className="confirm-delete-button"
                    onClick={() => setShowDeleteConfirmation(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="cancel-button"
                    onClick={() => handleDeleteDietPlan(dietPlanToDelete!)}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}

          {(showCreateDietPopup || editDietPlan) && (
            <div className="create-diet-popup">
              <div className="create-diet-popup-content">
                <h2>
                  {editDietPlan ? "Edit Diet Plan" : "Create New Diet Plan"}
                </h2>
                <div
                  style={{
                    display: "flex",

                    alignItems: "center",
                  }}
                >
                  <div className="switch-container">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={isPureVegDiet}
                        onChange={handleVegToggle}
                      />
                      <span className="slider round"></span>
                    </label>
                    {editDietPlan ? (
                      <p className="active-inactive-text">
                        {isPureVegDiet ? "Pure Veg" : "Non Veg"}
                      </p>
                    ) : (
                      <p className="active-inactive-text">
                        {isPureVegDiet ? "Pure Veg" : "Non Veg"}
                      </p>
                    )}
                  </div>
                  <div className="language-dropdown">
                    <label htmlFor="language" className="language-label">
                      Diet Plan Language:
                    </label>
                    <select
                      id="language"
                      className="language-select"
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                    >
                      <option value="" disabled>
                        Select a language
                      </option>
                      {languages.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <input
                  type="text"
                  value={newDietCategory}
                  placeholder="Diet Category"
                  onChange={(e) => setNewDietCategory(e.target.value)}
                />
                {newMealPlans.map((meal, index) => (
                  <div className="meal-container" key={index}>
                    <div style={{ display: "grid" }}>
                      <span className="meal-container-index">{index + 1}</span>
                      <img
                        className="meal-container-delete-particular-meal"
                        onClick={() => deleteMeal(index)}
                        src={closeIcon}
                        alt="Delete"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Meal Type"
                      value={meal.mealType}
                      onChange={(e) =>
                        updateMeal(index, "mealType", e.target.value)
                      }
                    />
                    <ReactQuill
                      value={meal.diet}
                      onChange={(value) => updateMeal(index, "diet", value)}
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, 4, 5, 6, false] }],
                          ["bold", "italic", "underline"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [{ align: [] }],
                          ["image"],
                          ["clean"], // Remove formatting
                        ],
                      }}
                      formats={[
                        "header",
                        "bold",
                        "italic",
                        "underline",
                        "blockquote",
                        "code-block",
                        "list",
                        "bullet",
                        "align",
                        "link",
                        "image",
                      ]}
                    />
                  </div>
                ))}
                <button className="add-meal-button" onClick={addMeal}>
                  + Add Meal
                </button>
                <div className="create-diet-popup-content-sav-cancel-btn">
                  <button
                    className="cancel-create-button"
                    style={{ background: "grey" }}
                    onClick={() => {
                      setShowCreateDietPopup(false);
                      setEditDietPlan(null);
                      setNewDietCategory("");
                      setNewMealPlans([]);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="create-button"
                    onClick={
                      editDietPlan ? handleUpdateDietPlan : handleCreateDietPlan
                    }
                  >
                    {editDietPlan ? "Update Diet Plan" : "Create Diet Plan"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DietPanel;
